import { PropsWithChildren } from 'react';
import classNames from 'classnames';
import { parseDate } from '@progress/kendo-intl';
import { AnimatePresence, motion } from 'framer-motion';

import { Competency } from './competency';
import { CommentAction } from './comment-action';
import { PlayerInitials } from './player-initials';
import { Assessment } from '../../types/assessment';
import { GetResourceValue } from '../../features/assessment/assessment-document';
import {
  Action,
  AssessmentState,
  Response,
  Measurable
} from '../../types/assessment-state';

interface MeasurableItemProps {
  document: Assessment;
  state: AssessmentState;
  id: string;
  titleResId?: string;
  descriptionResId?: string;
  averageScore?: number | undefined;
  actions?: Action[];
  responses?: Response[];
}

export const MeasurableItem = ({
  document,
  state,
  actions = [],
  responses = [],
  ...props
}: MeasurableItemProps) => {
  return (
    <Competency
      className={'mt-3'}
      title={GetResourceValue(document, props.titleResId ?? '')}
      description={GetResourceValue(document, props.descriptionResId ?? '')}
      rating={props.averageScore}>
      <AnimatePresence>
        {actions.map((res, index) => {
          const dueDate = parseDate(res.targetDate);
          const assignee = res.assignee
            ? state.players?.find((i) => i.code === res.assignee)
            : { name: 'Unassigned', code: undefined };
          return (
            <>
              <CommentAction
                key={index}
                votes={res.totalVotes}
                dueDate={dueDate}
                status={res.status}
                assignee={res.assignee ? assignee?.name : '--'}>
                {res.name}
              </CommentAction>
              <ActionMeasures
                state={state}
                key={`am${index}`}
                actionId={res.id}
                measures={res.measurable}
              />
            </>
          );
        })}
      </AnimatePresence>
    </Competency>
  );
};

interface ActionMeasuresProps {
  state: AssessmentState;
  actionId: string;
  measures: Measurable[];
  className?: string | undefined;
}

const ActionMeasures = ({
  state,
  measures,
  className
}: ActionMeasuresProps) => {
  const actionMeasuresClass = classNames('action-measures', className);
  return (
    <div className={actionMeasuresClass}>
      <div className={'action-measures__title'}>Measures</div>
      <div className={'action-measures__description'}>
        What does success look like?
      </div>
      <AnimatePresence>
        {measures.map((measurable) => (
          <ActionMeasure key={measurable.id}>
            <PlayerInitials
              state={state}
              playerId={measurable.playerCode}
              className={'me-2'}
            />
            <div className={'flex-grow-1'}>{measurable.comment}</div>
          </ActionMeasure>
        ))}
      </AnimatePresence>
    </div>
  );
};

interface ActionMeasureProps extends PropsWithChildren {
  className?: string | undefined;
}

const ActionMeasure = ({ children, className }: ActionMeasureProps) => {
  const actionMeasureClass = classNames('action-measure', className);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, x: '50vw' }}
      className={actionMeasureClass}>
      {children}
    </motion.div>
  );
};
